/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreHttp from "@azure/core-http";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { IdentityRestClient } from "../identityRestClient";
import {
  CommunicationIdentityCreateResponse,
  CommunicationIdentityUpdateRequest,
  CommunicationTokenRequest,
  CommunicationIdentityIssueTokenResponse
} from "../models";

/**
 * Class representing a CommunicationIdentity.
 */
export class CommunicationIdentity {
  private readonly client: IdentityRestClient;

  /**
   * Initialize a new instance of the class CommunicationIdentity class.
   * @param client Reference to the service client
   */
  constructor(client: IdentityRestClient) {
    this.client = client;
  }

  /**
   * Create a new identity.
   * @param options The options parameters.
   */
  create(options?: coreHttp.OperationOptions): Promise<CommunicationIdentityCreateResponse> {
    const operationOptions: coreHttp.RequestOptionsBase = coreHttp.operationOptionsToRequestOptionsBase(
      options || {}
    );
    return this.client.sendOperationRequest(
      { options: operationOptions },
      createOperationSpec
    ) as Promise<CommunicationIdentityCreateResponse>;
  }

  /**
   * Delete the identity, revoke all tokens of the identity and delete all associated data.
   * @param id Identifier of the identity to be deleted.
   * @param options The options parameters.
   */
  delete(id: string, options?: coreHttp.OperationOptions): Promise<coreHttp.RestResponse> {
    const operationOptions: coreHttp.RequestOptionsBase = coreHttp.operationOptionsToRequestOptionsBase(
      options || {}
    );
    return this.client.sendOperationRequest(
      { id, options: operationOptions },
      deleteOperationSpec
    ) as Promise<coreHttp.RestResponse>;
  }

  /**
   * Update an Identity.
   * @param id Identifier of the identity.
   * @param body The properties of the identity to be updated.
   * @param options The options parameters.
   */
  update(
    id: string,
    body: CommunicationIdentityUpdateRequest,
    options?: coreHttp.OperationOptions
  ): Promise<coreHttp.RestResponse> {
    const operationOptions: coreHttp.RequestOptionsBase = coreHttp.operationOptionsToRequestOptionsBase(
      options || {}
    );
    return this.client.sendOperationRequest(
      { id, body, options: operationOptions },
      updateOperationSpec
    ) as Promise<coreHttp.RestResponse>;
  }

  /**
   * Generate a new token for an identity.
   * @param id Identifier of the identity to issue token for.
   * @param body Requesting scopes for the new token.
   * @param options The options parameters.
   */
  issueToken(
    id: string,
    body: CommunicationTokenRequest,
    options?: coreHttp.OperationOptions
  ): Promise<CommunicationIdentityIssueTokenResponse> {
    const operationOptions: coreHttp.RequestOptionsBase = coreHttp.operationOptionsToRequestOptionsBase(
      options || {}
    );
    return this.client.sendOperationRequest(
      { id, body, options: operationOptions },
      issueTokenOperationSpec
    ) as Promise<CommunicationIdentityIssueTokenResponse>;
  }
}
// Operation Specifications

const serializer = new coreHttp.Serializer(Mappers, /* isXml */ false);

const createOperationSpec: coreHttp.OperationSpec = {
  path: "/identities",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.CommunicationIdentity
    }
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  serializer
};
const deleteOperationSpec: coreHttp.OperationSpec = {
  path: "/identities/{id}",
  httpMethod: "DELETE",
  responses: { 204: {} },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.id],
  serializer
};
const updateOperationSpec: coreHttp.OperationSpec = {
  path: "/identities/{id}",
  httpMethod: "PATCH",
  responses: { 204: {} },
  requestBody: Parameters.body,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.id],
  headerParameters: [Parameters.contentType],
  mediaType: "json",
  serializer
};
const issueTokenOperationSpec: coreHttp.OperationSpec = {
  path: "/identities/{id}/token",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.CommunicationIdentityToken
    }
  },
  requestBody: Parameters.body1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.id],
  headerParameters: [Parameters.contentType1],
  mediaType: "json",
  serializer
};
